import { useReducer, useRef, useCallback } from 'react';
// No need to import type-only imports in JavaScript
import { /* periodPresetsT */ } from '../../models/period';
import {
  REDUCER_ACTIONS,
  UPDATE_FILTER_OPTIONS_ACTIONS,
} from '../../constants/campaigns';
import moment from 'moment';
import { getDayEnd, getDayStart } from '../../utils/dates';
import * as PeriodModel from '../../models/period';
import * as PERIOD_PRESETS from '../../constants/period-presets';
import * as PREVIOUS_PERIOD_PRESETS from '../../constants/previous-period-presets';
import { campaignReducer } from '../../utils/campaignHelpers';
import React, { createContext, useContext } from 'react';
import { useBreakdownOptions } from '../../hooks/use-breakdown-options';

const FilterOptionsContext = createContext(null);

export const FilterOptionsProvider = ({ children, locations, showStaffFilters, disableAgeAndGenderOnFrontEnd }) => {
  const availableBreakdowns = useBreakdownOptions(showStaffFilters, disableAgeAndGenderOnFrontEnd)

  const testPeriod = PeriodModel.getExplorerPeriod(PERIOD_PRESETS.last7Days);
  const controlPeriod = PeriodModel.getExplorerComparePeriod(PREVIOUS_PERIOD_PRESETS.previousPeriod, { start: testPeriod.selectedDates.start, end: testPeriod.selectedDates.end });

  const defaultBreakdown = [{ key: availableBreakdowns[0].key, values: [availableBreakdowns[0].values[0]] }]

  const [state, dispatch] = useReducer(campaignReducer, {
    filterOptions: {
      metric: {
        key: 'entries',
        display: 'Store > Entries',
        unitType: 'number'
      },
      taxonomies: [],
      lineTaxonomies: [],
      testLocations: [locations[0].id],
      controlLocations: [],
      testPeriod,
      controlPeriod,
      availableBreakdowns,
      selectedBreakdowns: defaultBreakdown,
      name: 'New Campaign'
    },
    data: {},
    dataUplift: {},
    taxonomyList: [],
    taxonomyKeyStrings: [],
    lineTaxonomyList: [],
    lineTaxonomyKeyStrings: [],
    prevFetchedFilterOptions: undefined,
    enabledLocations: locations.map((l) => l.id),
    defaultBreakdown,
    ctx: null,
  });

  const ctxFetched = useRef(false);

  const updateFilterOptions = useCallback(
    (type, value) => {
      switch (type) {
        case UPDATE_FILTER_OPTIONS_ACTIONS.METRIC:
          dispatch({
            type: REDUCER_ACTIONS.SET_FILTER_OPTIONS,
            payload: { ...state.filterOptions, metric: value },
          });
          break;

        case UPDATE_FILTER_OPTIONS_ACTIONS.TAXONOMIES:
          dispatch({
            type: REDUCER_ACTIONS.SET_FILTER_OPTIONS,
            payload: { ...state.filterOptions, taxonomies: value },
          });
          break;

        case UPDATE_FILTER_OPTIONS_ACTIONS.LINE_TAXONOMIES:
          dispatch({
            type: REDUCER_ACTIONS.SET_FILTER_OPTIONS,
            payload: { ...state.filterOptions, lineTaxonomies: value },
          });
          break;

        case UPDATE_FILTER_OPTIONS_ACTIONS.TEST_LOCATIONS:
          dispatch({
            type: REDUCER_ACTIONS.SET_FILTER_OPTIONS,
            payload: { ...state.filterOptions, testLocations: value },
          });
          break;

        case UPDATE_FILTER_OPTIONS_ACTIONS.CONTROL_LOCATIONS:
          dispatch({
            type: REDUCER_ACTIONS.SET_FILTER_OPTIONS,
            payload: { ...state.filterOptions, controlLocations: value },
          });
          break;

        case UPDATE_FILTER_OPTIONS_ACTIONS.TEST_PERIOD:
          const controlPeriod = PeriodModel.getExplorerComparePeriod(PREVIOUS_PERIOD_PRESETS.previousPeriod, { start: value.selectedDates.start, end: value.selectedDates.end });
          dispatch({
            type: REDUCER_ACTIONS.SET_FILTER_OPTIONS,
            payload: {
              ...state.filterOptions,
              testPeriod: value,
              controlPeriod
            },
          });
          break;

        case UPDATE_FILTER_OPTIONS_ACTIONS.CONTROL_PERIOD:
          dispatch({
            type: REDUCER_ACTIONS.SET_FILTER_OPTIONS,
            payload: {
              ...state.filterOptions,
              controlPeriod: value
            },
          });
          break;

        case UPDATE_FILTER_OPTIONS_ACTIONS.BREAKDOWN:
          dispatch({
            type: REDUCER_ACTIONS.SET_FILTER_OPTIONS,
            payload: { ...state.filterOptions, selectedBreakdowns: value },
          });
          break;

        case UPDATE_FILTER_OPTIONS_ACTIONS.NAME:
          dispatch({
            type: REDUCER_ACTIONS.SET_FILTER_OPTIONS,
            payload: { ...state.filterOptions, name: value },
          });
          break;


        case REDUCER_ACTIONS.SET_DATA_TEST_TEST:
          dispatch({
            type: REDUCER_ACTIONS.SET_DATA,
            payload: { ...state.data, test_test: value }
          });
          break;

        case REDUCER_ACTIONS.SET_DATA_TEST_CONTROL:
          dispatch({
            type: REDUCER_ACTIONS.SET_DATA,
            payload: { ...state.data, test_control: value }
          });
          break;

        case REDUCER_ACTIONS.SET_DATA_CONTROL_TEST:
          dispatch({
            type: REDUCER_ACTIONS.SET_DATA,
            payload: { ...state.data, control_test: value }
          });
          break;

        case REDUCER_ACTIONS.SET_DATA_CONTROL_CONTROL:
          dispatch({
            type: REDUCER_ACTIONS.SET_DATA,
            payload: { ...state.data, control_control: value }
          });
          break;

        default:
          if (Object.values(REDUCER_ACTIONS).includes(type)) {
            dispatch({ type, payload: value });
          }
          break;
      }

    },
    [state, locations],
  );

  return (
    <FilterOptionsContext.Provider
      value={{
        state,
        updateFilterOptions,
        ctxFetched,
      }}
    >
      {children}
    </FilterOptionsContext.Provider>
  );
};

export const useFilterOptionsContext = () => {
  const context = useContext(FilterOptionsContext);

  if (!context) {
    throw new Error(
      'useFilterOptionsContext must be used within a FilterOptionsProvider',
    );
  }
  return context;
};

export const useHeatmapDisplayGridContext = () => {
  const context = useContext(FilterOptionsContext);
  const { cardSize, prevFetchedFilterOptions } = context.state;
  // when the date range is changed
  // if the date range is outside of the previously fetched date range
  // then show no heatmaps out of range message
  // we can't fully decouple this from the display grid
  // as the date range is used in the heatmap display grid
  // de-coupling this will confuse users

  const { selectedDate } = context.state.filterOptions;
  if (!context) {
    throw new Error(
      'useHeatmapDisplayGridContext must be used within a FilterOptionsProvider',
    );
  }
  return {
    cardSize,
    selectedDate,
    taxonomies: prevFetchedFilterOptions?.taxonomies || [],
    prevFetchedFilterOptions,
  };
};
